import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import { CurrencyEnums, FormEnums } from "models/enums";
import { FormHandles } from "models/propTypes/genericFormProps";
import { ArticleFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { ArticleCreateFormViewProps } from "models/propTypes/articlePropTypes";
import { FormFieldConstants } from "utils/constants";
import { FormikProps } from "formik";
//#endregion
//#region components
import { GenericForm } from "components/common";
import { useInvoiceService } from "serviceHooks";
import NotVatRegisteredVatDescription from "./_fields/notVatRegisteredVatDescription";
//#endregion

const ArticleCreateForm: ForwardRefRenderFunction<FormHandles, ArticleCreateFormViewProps> = (props, ref) => {
  const invoiceService = useInvoiceService();
  const { units, vatTypes, revenueAccounts, noVatCompany } = props;

  const getRevenueAccountItems = (
    value: string,
    formattedValue?: string,
    formikProps?: FormikProps<ArticleFormTypes.Article>
  ) =>
    revenueAccounts?.accounts
      ?.filter((account) => account.vat === formikProps?.values.vat)
      .map((account) => ({
        id: account.number,
        value: account.number.toString(),
        description: account.name,
      })) ?? [];

  return (
    <GenericForm<ArticleFormTypes.Article>
      formRef={ref}
      {...props}
      fields={{
        name: {
          label: TranslationKeys.Common.Name,
          type: FormEnums.FormFieldType.TEXT,
          descriptionText: TranslationKeys.Article.NameDescription,
        },
        unit: {
          label: TranslationKeys.Invoice.Unit,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          descriptionText: TranslationKeys.Article.UnitDescription,
          extraProps: {
            referenceFieldName: FormFieldConstants.Article.Article.UnitId,
            suggestedItems: units,
            includeNone: true,
          },
        },
        unitPrice: {
          label: TranslationKeys.Common.StandardPrice,
          type: FormEnums.FormFieldType.CURRENCY,
          descriptionText: TranslationKeys.Article.UnitPriceDescription,
          extraProps: {
            currency: CurrencyEnums.CurrencyCode.SEK,
          },
        },
        vat: {
          label: TranslationKeys.Common.VatRate,
          type: FormEnums.FormFieldType.CHOICE,
          descriptionText: noVatCompany ? <NotVatRegisteredVatDescription /> : TranslationKeys.Article.VatDescription,
          extraProps: {
            items: vatTypes,
            // Not VAT registered: keep the chips but grey out every rate except Momsfritt (same as ROT/RUT chips).
            disabledItems: noVatCompany
              ? vatTypes.filter((type) => type.id !== 0).map((type) => Number(type.id))
              : undefined,
          },
          onChangeHandler: (_name: string, _value: any, formikProps: FormikProps<ArticleFormTypes.Article>) => {
            formikProps.setFieldValue(FormFieldConstants.Article.Article.Account, undefined);
            formikProps.setFieldValue(FormFieldConstants.Article.Article.AccountName, undefined);
          },
        },
        euVatCategoryId: {
          label: TranslationKeys.Article.TypeOfArticle,
          type: FormEnums.FormFieldType.CHOICE,
          descriptionText: TranslationKeys.Article.EuVatTypeDescription,
          extraProps: {
            items: invoiceService.getEuVatCategoryTypes(),
          },
        },
        accountName: {
          label: TranslationKeys.Article.RevenueAccount,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          descriptionText: TranslationKeys.Article.RevenueAccountDescription,
          hidden: revenueAccounts?.enabled !== true,
          extraProps: {
            referenceFieldName: FormFieldConstants.Article.Article.Account,
            items: getRevenueAccountItems,
          },
        },
      }}
    />
  );
};

export default forwardRef(ArticleCreateForm);
